import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { Params } from '@angular/router';
import { TableFilterData, TableFilterDataOption } from '@app/models';
import { TOOLTIP_DELAY } from '@app/shared/data/values';

@Component({
    selector: 'app-custom-table-filter',
    templateUrl: './custom-table-filter.component.html',
    styleUrls: ['./custom-table-filter.component.scss'],
    standalone: false
})
export class CustomTableFilterComponent implements OnInit {
  
  tooltipDelay = TOOLTIP_DELAY.DATA_TABLE;
  @Input() filterFormOpened: boolean = false;
  @Input() customTableFilterData!: TableFilterData[];
  @Input() hasFilter: boolean = false;
  @Input() hasTableStyles: boolean = true;
  @Output() filterValueChange = new EventEmitter<Params>();

  filterForm: UntypedFormGroup = this._formBuilder.group({});
  formData: { [k: string]: TableFilterData } | null = null;
  previousFilerValues: Params = {};

  constructor(
    private readonly _formBuilder: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.filterForm.valueChanges.subscribe((data: { [k: string]: TableFilterData}) => {
      this.formData = data;
    });
  }

  createForm(filterData?: TableFilterData[]) {
    if (filterData) this.customTableFilterData = filterData;

    if (this.customTableFilterData?.length) {
      this.filterForm = this._formBuilder.group(this.getAsFormGroup());
      this.formData = this.filterForm.value;
    } else {
      this.filterForm = this._formBuilder.group({});
      this.hasFilter = false;
      this.formData = null;
    }

  }

  /**
   * To create a dynamic formgroup from array of Object
   * @returns Formgroup with dynamic form groups
   */
  private getAsFormGroup(): UntypedFormGroup {
    let formObj: { [k: string]: any } = {};
    this.hasFilter = false;

    this.customTableFilterData.forEach(filterGroup => {
      let selectedOpts = filterGroup.options.filter(opt => opt.checked);
      this.hasFilter = (!!selectedOpts?.length) || this.hasFilter;
      if (this.hasFilter && selectedOpts?.length) {
        this.previousFilerValues[filterGroup.value] = filterGroup.multiSelect ? selectedOpts.map(opt => opt.value) : selectedOpts[0].value;
      }

      let selectedOptsArr = (selectedOpts?.length ? [selectedOpts[0]] : []);
      formObj[filterGroup.value] = this._formBuilder.group({
        ...filterGroup, 
        options: this._formBuilder.control(filterGroup.multiSelect ? selectedOpts : selectedOptsArr)
      });
    });
    if (!this.hasFilter) this.previousFilerValues = {};

    return formObj as UntypedFormGroup;
  }

  setCheckBoxValue(groupValues: TableFilterData, optValue: TableFilterDataOption, checkBox: MatCheckbox) {
    const groupValue: TableFilterData = this.formControls[groupValues.value].value;
    let modifiedOpts = groupValue.options;

    if (modifiedOpts.some(mopt => mopt.value ==  optValue.value)) {
      modifiedOpts = modifiedOpts.filter(opt => opt.value != optValue.value).map(opt => ({...opt, checked: opt.checked}));
    } else {
      modifiedOpts = [ ...modifiedOpts, optValue ];
    }

    if (!groupValues.multiSelect) {
      modifiedOpts = !checkBox.checked ? [] : [{...optValue, checked: true}];
    }

    this.filterForm.patchValue({  
      [groupValues.value]: { 
        ...groupValue, 
        options: modifiedOpts
      } 
    });
    this.formData = this.filterForm.value;
  }

  private getFitlerParams() {
    let filterParams: Params = {};
    this.formData = this.filterForm.value;

    if (this.formData) {

      const handleMultiSelect = (multiSelect: boolean, values: string[]) => {
        if (multiSelect || values.length > 1) {
          return values;
        } 
          return values[0];
      }

      for (const key in this.formData) {
        if (this.formData.hasOwnProperty(key)) {
          let values = this.formData[key].options.map(opt => opt.value);

          if (values?.length) {
            filterParams[key] = handleMultiSelect(this.formData[key].multiSelect, values);      
          }
        }
      }
    }

    return filterParams;
  }

  submitFilters() {
    let filterParams: Params = this.getFitlerParams();
    
    if (JSON.stringify(this.previousFilerValues) != JSON.stringify(filterParams)) {
      this.hasFilter = true;
      this.previousFilerValues = filterParams;
      this.filterValueChange.emit(filterParams);
      if (Object.keys(filterParams).length === 0) {
        this.hasFilter = false;
      }
    }
  }

  clearGroupValue(e: any, groupName: string) {
    if (e) e.stopPropagation();
    
    this.formControls[groupName].setValue({
      ...this.formControls[groupName].value, 
      options: []
    });
    this.formData = this.filterForm.value;
  }

  clearAllGroups() {
    this.customTableFilterData.forEach((tFilterData, i) => {
      this.formControls[tFilterData.value].setValue({
        ...this.formControls[tFilterData.value].value, 
        options: []
      });
    });
    this.previousFilerValues = {};
    this.filterValueChange.emit({});
    this.hasFilter = false;
  }

  isChecked(checkOption: TableFilterDataOption, groupName: string): boolean {
    if (this.filterForm?.value[groupName]?.options?.length) {
      return this.filterForm.value[groupName].options.some((opt: TableFilterDataOption) => opt.value == checkOption.value);
    }
    return false;
  }
  
  get hasAnyFilter(): boolean {
    return this.hasFilter;
  }

  get formControls() {
    return this.filterForm.controls;
  }

}